import React, { Fragment, useEffect, useMemo, useState } from "react";
import {
  Lightning, CaretRight, CaretDown, CaretUp, X, Bell, ArrowUpRight,
  CalendarBlank, Clock, Pulse,
  FileText, Wallet, SlidersHorizontal, GitBranch, Prohibit, Scroll, Bank,
} from "@phosphor-icons/react";
import { useGov } from "@/hooks/governance";
import { API, http } from "@/lib/constants";
import { govActionLabel } from "@/lib/governance";
import { humanLeft, useWatchlist, removeWatch, backfillCloseTs } from "@/lib/voteWatch";
import { WatchButton } from "@/components/governance/WatchButton";
import { cn } from "@/lib/format";

const EPOCH_FALLBACK_SECONDS = 432000;
const MAX_ROWS = 8;
const C_YES = "#34d399";
const C_NO = "#f87171";
const C_ABS = "#a855f7";

const TYPE_META = {
  info_action: { icon: FileText, color: "#22d3ee" },
  treasury_withdrawals: { icon: Wallet, color: "#e879f9" },
  parameter_change: { icon: SlidersHorizontal, color: "#a855f7" },
  hard_fork_initiation: { icon: GitBranch, color: "#f59e0b" },
  no_confidence: { icon: Prohibit, color: "#f87171" },
  new_constitution: { icon: Scroll, color: "#34d399" },
  new_committee: { icon: Bank, color: "#38bdf8" },
  _default: { icon: Lightning, color: "#22d3ee" },
};

const StatusChip = ({ status, ended }) => {
  const color = ended ? "#94a3b8" : "#22d3ee";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] mono"
      style={{ borderColor: `${color}66`, color }}
    >
      {status}
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color, boxShadow: ended ? "none" : `0 0 6px ${color}` }} />
    </span>
  );
};

const Legend = ({ color, text }) => (
  <span className="inline-flex items-center gap-1.5 text-muted-foreground">
    <span className="h-2 w-2 rounded-full" style={{ background: color }} />
    {text}
  </span>
);

const VoteBar = ({ label, v, loading }) => {
  const yes = v?.yes || 0, no = v?.no || 0, ab = v?.abstain || 0;
  const tot = yes + no + ab;
  const pc = (n) => (tot ? Math.round((n / tot) * 100) : 0);
  return (
    <div className="space-y-2 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.18em] mono text-muted-foreground">{label}</div>
      <div className="flex h-2 rounded-full overflow-hidden bg-white/[0.06]">
        {tot ? (
          <>
            <div style={{ width: `${(yes / tot) * 100}%`, background: C_YES }} />
            <div style={{ width: `${(no / tot) * 100}%`, background: C_NO }} />
            <div style={{ width: `${(ab / tot) * 100}%`, background: C_ABS }} />
          </>
        ) : null}
      </div>
      {tot ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] mono">
          <Legend color={C_YES} text={`Yes ${yes} (${pc(yes)}%)`} />
          <Legend color={C_NO} text={`No ${no} (${pc(no)}%)`} />
          <Legend color={C_ABS} text={`Abstain ${ab} (${pc(ab)}%)`} />
        </div>
      ) : (
        <div className="text-[11px] mono text-muted-foreground/70">{loading ? "Loading votes…" : "No votes recorded yet"}</div>
      )}
    </div>
  );
};

const SummaryRow = ({ icon: Icon, label, value, valueClass }) => (
  <div className="flex items-center gap-2.5">
    <Icon size={15} className="text-primary/70 shrink-0" />
    <span className="text-[9px] uppercase tracking-[0.12em] mono text-muted-foreground w-[74px] shrink-0">{label}</span>
    <span className={cn("text-[12px] mono tabnum text-foreground", valueClass)}>{value}</span>
  </div>
);

const LG_COLS = "lg:grid-cols-[1.6fr_2.6fr_1.1fr_1.1fr_1.1fr_56px]";

// Home-page "closing soonest" governance panel — Option 3: expandable table.
// Rows list the soonest-closing live actions; expanding one reveals DREP/SPO/CC
// voting bars and a summary. Vote tallies and close times come from live data.
export const GovDigest = ({ onGovSearch, title = "Governance · closing soonest", showWatchlist = true, testid = "gov-digest" }) => {
  const { data, loading } = useGov("/governance/proposals", { params: { count: 40, status: "active" } });
  const latest = useGov("/epochs/latest");
  const cur = latest.data;
  const prev = useGov(`/epochs/${(cur?.epoch ?? 1) - 1}`, { enabled: cur?.epoch != null });
  const watchlist = useWatchlist();
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(null);
  const [details, setDetails] = useState({});

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const epochLen = useMemo(() => {
    if (prev.data?.start_time && cur?.start_time && cur.start_time > prev.data.start_time) {
      return cur.start_time - prev.data.start_time;
    }
    return EPOCH_FALLBACK_SECONDS;
  }, [cur, prev.data]);

  const closeTsFor = useMemo(() => (expires) => {
    if (expires == null || !cur?.start_time || cur?.epoch == null) return null;
    return (cur.start_time + (expires + 1 - cur.epoch) * epochLen) * 1000;
  }, [cur, epochLen]);

  const rows = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return list
      .slice()
      .sort((a, b) => (a.expires_after_epoch ?? 1e9) - (b.expires_after_epoch ?? 1e9))
      .slice(0, MAX_ROWS)
      .map((p) => ({
        key: `${p.tx_hash}:${p.cert_index}`,
        txHash: p.tx_hash,
        certIndex: p.cert_index,
        type: p.type,
        status: p.status,
        title: (p.title || "").trim() || govActionLabel(p.type),
        submitted: p.submitted_epoch,
        expires: p.expires_after_epoch,
        closeTs: closeTsFor(p.expires_after_epoch),
      }));
  }, [data, closeTsFor]);

  const idsKey = rows.map((r) => r.key).join(",");

  // fetch vote tallies (only on the detail endpoint) for the visible rows
  useEffect(() => {
    if (!rows.length) return;
    let cancelled = false;
    const missing = rows.filter((r) => !details[r.key]);
    if (!missing.length) return;
    Promise.all(
      missing.map((r) =>
        http.get(`${API}/governance/proposals/${r.txHash}/${r.certIndex}`)
          .then((res) => [r.key, res.data])
          .catch(() => null),
      ),
    ).then((entries) => {
      if (cancelled) return;
      const add = {};
      entries.forEach((e) => { if (e) add[e[0]] = e[1]; });
      if (Object.keys(add).length) setDetails((prevD) => ({ ...prevD, ...add }));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const watched = useMemo(() => Object.values(watchlist || {}).sort((a, b) => (a.closeTs ?? 1e15) - (b.closeTs ?? 1e15)), [watchlist]);

  useEffect(() => {
    const ready = rows.filter((t) => t.closeTs != null);
    if (ready.length) backfillCloseTs(ready);
  }, [rows]);

  if (loading && !data) {
    return <div data-testid="gov-digest-loading" className="h-[160px] rounded-2xl border border-primary/15 bg-primary/[0.03] animate-pulse" />;
  }
  if (!rows.length && !watched.length) return null;

  const go = (r) => onGovSearch?.(`/governance/proposals/${r.txHash}/${r.certIndex}`);
  const left = (closeTs) => {
    if (!closeTs) return "—";
    const r = closeTs - now;
    return r <= 0 ? "closed" : humanLeft(r);
  };

  return (
    <section data-testid={testid} className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/50 backdrop-blur-md box-glow p-4 sm:p-5 space-y-4">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Lightning size={16} weight="fill" className="text-[#22d3ee]" />
          <span className="text-[11px] uppercase tracking-[0.25em] mono text-[#22d3ee]">{title}</span>
        </div>
        <button
          type="button"
          data-testid="gov-digest-all"
          onClick={() => onGovSearch?.("/governance/proposals/live")}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] mono text-muted-foreground hover:text-foreground transition-colors"
        >
          all live actions <CaretRight size={11} />
        </button>
      </div>

      {rows.length ? (
        <div data-testid="gov-digest-table" className="rounded-xl border border-primary/15 overflow-hidden">
          {/* column header (desktop) */}
          <div className={cn("hidden lg:grid gap-3 px-4 py-2.5 border-b border-primary/10 bg-black/20 text-[9px] uppercase tracking-[0.14em] mono text-muted-foreground whitespace-nowrap", LG_COLS)}>
            <div>Type</div><div>Title</div><div>Status</div>
            <div>Submitted</div><div>Expires</div><div className="text-right">Actions</div>
          </div>

          {rows.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META._default;
            const Icon = meta.icon;
            const accent = meta.color;
            const det = details[r.key];
            const vc = det?.vote_counts;
            const detLoading = !det;
            const ended = r.closeTs != null && r.closeTs - now <= 0;
            const isOpen = open === r.key;
            const toggle = () => setOpen(isOpen ? null : r.key);
            return (
              <Fragment key={r.key}>
                {/* ---- desktop row ---- */}
                <div
                  data-testid={`gov-digest-item-${r.txHash}`}
                  className={cn("hidden lg:grid gap-3 items-center px-4 py-3 border-b border-primary/10 transition-colors", isOpen ? "bg-primary/[0.05]" : "hover:bg-white/[0.02]", LG_COLS)}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="grid place-items-center h-8 w-8 rounded-lg shrink-0" style={{ background: `${accent}1a`, border: `1px solid ${accent}55` }}>
                      <Icon size={16} weight="duotone" style={{ color: accent }} />
                    </span>
                    <span className="text-[12px] font-medium text-foreground truncate">{govActionLabel(r.type)}</span>
                  </div>
                  <button type="button" onClick={() => go(r)} className="text-left text-[12px] text-muted-foreground hover:text-[#22d3ee] transition-colors truncate" title={r.title}>
                    {r.title}
                  </button>
                  <div><StatusChip status={r.status} ended={ended} /></div>
                  <div className="text-[12px] mono tabnum text-foreground">epoch {r.submitted ?? "—"}</div>
                  <div className="text-[12px] mono tabnum text-foreground">epoch {r.expires ?? "—"}</div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      data-testid={`gov-digest-view-${r.txHash}`}
                      onClick={toggle}
                      aria-expanded={isOpen}
                      aria-label={isOpen ? "Collapse details" : "Expand details"}
                      className="inline-flex items-center justify-center h-7 w-7 rounded-lg border border-primary/30 text-primary hover:border-[#22d3ee]/60 hover:text-[#22d3ee] transition-colors"
                    >
                      {isOpen ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
                    </button>
                  </div>
                </div>

                {/* ---- mobile row ---- */}
                <div
                  data-testid={`gov-digest-item-m-${r.txHash}`}
                  className={cn("lg:hidden flex items-center gap-3 px-3 py-3 border-b border-primary/10", isOpen && "bg-primary/[0.05]")}
                >
                  <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0" style={{ background: `${accent}1a`, border: `1px solid ${accent}55` }}>
                    <Icon size={17} weight="duotone" style={{ color: accent }} />
                  </span>
                  <button type="button" onClick={() => go(r)} className="min-w-0 flex-1 text-left">
                    <div className="text-[10px] uppercase tracking-[0.1em] mono truncate" style={{ color: accent }}>{govActionLabel(r.type)}</div>
                    <div className="text-[12px] text-foreground font-medium truncate">{r.title}</div>
                    <div className="text-[10px] mono text-muted-foreground">{ended ? "voting closed" : `closes in ${left(r.closeTs)}`}</div>
                  </button>
                  <button
                    type="button"
                    data-testid={`gov-digest-view-m-${r.txHash}`}
                    onClick={toggle}
                    aria-expanded={isOpen}
                    className="grid place-items-center h-8 w-8 rounded-lg border border-primary/30 text-primary shrink-0"
                  >
                    {isOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
                  </button>
                </div>

                {/* ---- expanded detail ---- */}
                {isOpen ? (
                  <div data-testid={`gov-digest-detail-${r.txHash}`} className="grid lg:grid-cols-[1fr_1fr_260px] gap-5 px-4 py-5 border-b border-primary/10 bg-black/30 grid-bg">
                    <VoteBar label="DRep Voting" v={vc?.drep} loading={detLoading} />
                    <VoteBar label="Constitutional Committee" v={vc?.constitutional_committee} loading={detLoading} />
                    <div className="rounded-lg border border-primary/15 bg-black/20 p-3.5 space-y-3">
                      <SummaryRow icon={CalendarBlank} label="Submitted" value={`epoch ${r.submitted ?? "—"}`} />
                      <SummaryRow icon={Clock} label="Expires" value={`epoch ${r.expires ?? "—"}`} />
                      <SummaryRow icon={Pulse} label="Status" value={ended ? "Voting closed" : "Active for voting"} valueClass={ended ? "text-muted-foreground" : "text-[#22d3ee]"} />
                      <div className="flex items-center gap-2 pt-1">
                        <WatchButton item={r} tid={`gov-digest-watch-${r.txHash}`} disabled={r.closeTs == null} compact />
                        <button
                          type="button"
                          data-testid={`gov-digest-open-${r.txHash}`}
                          onClick={() => go(r)}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.1em] mono text-primary hover:text-[#22d3ee] transition-colors"
                        >
                          Open <ArrowUpRight size={12} weight="bold" />
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </Fragment>
            );
          })}
        </div>
      ) : null}

      {showWatchlist && watched.length ? (
        <div data-testid="gov-digest-watchlist" className="pt-1 space-y-2">
          <div className="flex items-center gap-2">
            <Bell size={12} className="text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-[0.22em] mono text-muted-foreground">Your watch list ({watched.length})</span>
          </div>
          <div className="flex flex-col gap-1.5">
            {watched.map((w) => (
              <div
                key={`${w.txHash}:${w.certIndex}`}
                data-testid={`gov-digest-watched-${w.txHash}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-primary/10 bg-black/20 px-3 py-2"
              >
                <button
                  type="button"
                  data-testid={`gov-digest-watched-link-${w.txHash}`}
                  onClick={() => onGovSearch?.(`/governance/proposals/${w.txHash}/${w.certIndex}`)}
                  className="min-w-0 flex-1 text-left truncate text-[12px] text-foreground hover:text-[#22d3ee] transition-colors"
                >
                  {w.title || govActionLabel(w.type)}
                </button>
                <span className="shrink-0 text-[11px] mono text-muted-foreground tabular-nums">{left(w.closeTs)}</span>
                <button
                  type="button"
                  data-testid={`gov-digest-watch-remove-${w.txHash}`}
                  onClick={() => removeWatch(w.txHash, w.certIndex)}
                  aria-label="Stop watching"
                  className="shrink-0 text-muted-foreground/70 hover:text-destructive transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};
